/**
 * Live overview dashboard (brief §5 Phase 1).
 *
 * Node health and active calls come from the WebSocket feed (pushed on every
 * change, so a node going offline shows within seconds); registrations,
 * trunks, and E911 warnings are polled over REST. When the feed is down the
 * node/call view falls back to polling `/nodes` and `/calls`.
 */

import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@pbxmgr/ui";
import {
  getCalls,
  getE911Warnings,
  getNodes,
  getRegistrations,
  getTrunks,
} from "@pbxmgr/api";

import { useDashboardLive } from "../dashboard/useDashboardLive";
import {
  DataTable,
  E911Warnings,
  NodeHealthTable,
  Panel,
} from "../dashboard/panels";

export function DashboardPage(): ReactNode {
  const { live, connected, lastFrameAt } = useDashboardLive();

  // Fallback polls for node health + calls, active only while the feed is down.
  const nodesPoll = useQuery({
    queryKey: ["dash", "nodes"],
    queryFn: getNodes,
    enabled: !connected,
    refetchInterval: 10_000,
  });
  const callsPoll = useQuery({
    queryKey: ["dash", "calls"],
    queryFn: getCalls,
    enabled: !connected,
    refetchInterval: 10_000,
  });

  // These have no live feed; poll them on a steady cadence.
  const registrations = useQuery({
    queryKey: ["dash", "registrations"],
    queryFn: getRegistrations,
    refetchInterval: 15_000,
  });
  const trunks = useQuery({
    queryKey: ["dash", "trunks"],
    queryFn: getTrunks,
    refetchInterval: 30_000,
  });
  const e911 = useQuery({
    queryKey: ["dash", "e911"],
    queryFn: getE911Warnings,
    refetchInterval: 15_000,
  });

  const nodes = connected && live ? live.nodes : (nodesPoll.data?.nodes ?? []);
  const calls = connected && live ? live.calls : (callsPoll.data?.calls ?? []);
  const online =
    connected && live ? live.online : (nodesPoll.data?.online ?? 0);
  const total =
    connected && live ? live.total : (nodesPoll.data?.total ?? nodes.length);
  const warnings = e911.data?.warnings ?? [];

  const nodesTone =
    total === 0 ? "unknown" : online === total ? "online" : "degraded";

  // Honest connection state: "polling" only while the REST fallback is
  // actually delivering. Feed down AND polls erroring = the numbers on
  // screen are stale — say so, with when they were last true, instead of
  // presenting them as current (the frozen-tab failure mode).
  const pollsFailing = !connected && (nodesPoll.isError || callsPoll.isError);
  const lastUpdatedMs = Math.max(
    lastFrameAt,
    nodesPoll.dataUpdatedAt ?? 0,
    callsPoll.dataUpdatedAt ?? 0,
  );
  const lastUpdated =
    lastUpdatedMs > 0 ? new Date(lastUpdatedMs).toLocaleTimeString() : null;

  return (
    <div className="dash">
      <div className="dash__head">
        <h1>Overview</h1>
        <div className="dash__status">
          <Badge tone={nodesTone}>
            {online} of {total} PBX nodes online
          </Badge>
          {pollsFailing ? (
            <Badge tone="offline">disconnected — data may be stale</Badge>
          ) : (
            <Badge tone={connected ? "online" : "degraded"}>
              {connected ? "live" : "polling"}
            </Badge>
          )}
          {pollsFailing && lastUpdated ? (
            <span className="ui-empty">last updated {lastUpdated}</span>
          ) : null}
        </div>
      </div>

      <Panel title={`E911 location warnings (${warnings.length})`}>
        <E911Warnings warnings={warnings} />
      </Panel>

      <Panel title="Node health">
        <NodeHealthTable nodes={nodes} />
      </Panel>

      <Panel title={`Active calls (${calls.length})`}>
        <DataTable rows={calls} hide={["node_id"]} />
      </Panel>

      <Panel title={`Registrations (${registrations.data?.count ?? 0})`}>
        <DataTable
          rows={registrations.data?.registrations ?? []}
          hide={["node_id"]}
          detail={["contact"]}
        />
      </Panel>

      <Panel title={`Trunks (${trunks.data?.count ?? 0})`}>
        <DataTable rows={trunks.data?.trunks ?? []} hide={["node_id"]} />
      </Panel>
    </div>
  );
}
